import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import TableComponent from '../../../../components/table/TableComponent';
import { PartnerForm } from './PartnerForm';
import { setDirection, setPage, setSortField } from '../../../../redux/slices/PaginationSlice';
import {
    createNewPartner,
    deletePartner,
    getPartnersPage,
    savePartnerContracts,
    updatePartner,
} from './PartnersService';
import { useOnMount } from '../../../../components/common/Hooks';
import { SearchFilter } from '../../../../components/filter/SearchFilter';
import { ConfirmationModal } from '../../../../components/modal/ConfirmationModal';
import { isMobile } from 'react-device-detect';
import { ToggleFilterButton } from '../../../../components/buttons/ToggleFilterButton';
import { PartnerContractsForm } from './contracts/PartnerContractsForm';
import { EditButton } from '../../../../components/buttons/EditButton';
import { DeleteButton } from '../../../../components/buttons/DeleteButton';

export const PartnersPage = () => {

    const { t, i18n } = useTranslation();
    const alert = useAlert();
    const onMount = useOnMount();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const pagination = useSelector((state) => state.pagination);
    const [filterValue, setFilterValue] = useState('');
    const [partners, setPartners] = useState([]);
    const [partner, setPartner] = useState({
        partnerName: '',
        address: '',
        companyCode: '',
        pvmCode: '',
        phoneNumber: '',
        email: '',
        contractValidTill: '',
        products: [],
    });
    const [partnerContracts, setPartnerContracts] = useState({
        partnerId: null,
        title: null,
        contracts: [],
    });
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const handleCloseModal = () => setIsConfirmOpen(false);

    useEffect(() => {
        if (onMount) {
            init();
        } else {
            loadPartners();
        }
    }, [pagination.page, pagination.sortByField, pagination.sortingDirection]);

    const init = () => {
        if (pagination.sortByField === 'partnerName'
            && pagination.page === 0
            && pagination.sortingDirection === 'ASC') {
            loadPartners();
        } else {
            dispatch(setSortField('partnerName'));
            dispatch(setPage(0));
            dispatch(setDirection('ASC'));
        }
    };

    const handleSubmit = (values, partnerId) => {
        partnerId
            ? update(values, partnerId)
            : insert(values);
    };

    const insert = (values) => {
        createNewPartner(values)
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                loadPartners();
            })
            .catch(() => alert.error(t('alert:saveError')));
    };

    const update = (values, partnerId) => {
        updatePartner({ ...values, id: partnerId })
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                loadPartners();
            })
            .catch(() => alert.error(t('alert:saveError')));
    };

    const handleCancel = () => {
        setPartner({
            partnerName: '',
            address: '',
            companyCode: '',
            pvmCode: '',
            phoneNumber: '',
            email: '',
            contractValidTill: '',
            products: [],
        });
    };

    const loadPartners = () => {
        let request = {
            page: pagination.page,
            size: pagination.size,
            ascDesc: pagination.sortingDirection,
            sortField: pagination.sortByField,
            lang: i18n.language.toUpperCase(),
            title: filterValue,
        };
        setLoading(true);
        getPartnersPage(request)
            .then((response) => setPartners(response.data))
            .catch(() => alert.error(t('alert:loadingError')))
            .finally(() => setLoading(false));
    };

    const openConfirmation = (partner) => {
        setItemToDelete(partner);
        setIsConfirmOpen(true);
    };

    const deleteRecord = () => {
        deletePartner(itemToDelete.id)
            .then(() => {
                alert.success(t('alert:deleteSuccess'));
                loadPartners();
            })
            .catch(() => alert.error(t('alert:deleteError')));
        handleCloseModal();
    };

    const editRecord = (partner) => {
        setPartner({
            id: partner.id,
            partnerName: partner.partnerName,
            address: partner.address,
            companyCode: partner.companyCode,
            pvmCode: partner.pvmCode,
            phoneNumber: partner.phoneNumber,
            email: partner.email,
            contractValidTill: partner.contractValidTill,
            products: partner.products != null
                ? partner.products.map(it => ({ label: it.title, value: it.id }))
                : [],

        });
        document.getElementById('createButton').click();
    };

    const editPartnerContracts = (partner) => {
        setPartnerContracts({
            partnerId: partner.id,
            title: partner.partnerName,
            contracts: partner.contracts,
        });
        setTimeout(() => document.getElementById('partner-contracts-show-div').click(), 100);
    };

    const handleContractsSubmit = (contracts) => {
        let request = {
            partnerId: partnerContracts.partnerId,
            contracts: contracts,
        };
        savePartnerContracts(request)
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                loadPartners();
            })
            .catch(() => alert.error(t('alert:saveError')))
            .finally(() => resetContracts());
    };

    const handleContractsCancel = () => {
        resetContracts();
    };

    const resetContracts = () => {
        setPartnerContracts({
            partnerId: null,
            title: null,
            contracts: [],
        });
    };

    const getButtons = () => {
        return [
            {
                element: <button className={'button--main primary-button'}
                                 onClick={editPartnerContracts}>{t('partner:contracts.button')}</button>,
            },
            { element: <EditButton onClick={editRecord} /> },
            { element: <DeleteButton onClick={openConfirmation} /> },
        ];
    };

    return (
        <>
            <div className={isMobile ? 'mobile-filter-container' : 'filter-container'}>
                <PartnerForm onClick={handleSubmit}
                             onCancel={handleCancel}
                             editingPartner={partner} />
                <PartnerContractsForm onSubmit={handleContractsSubmit}
                                      onCancel={handleContractsCancel}
                                      partnerTitle={partnerContracts.title}
                                      contracts={partnerContracts.contracts}></PartnerContractsForm>
                {isMobile ? <ToggleFilterButton></ToggleFilterButton> : <></>}
                <SearchFilter doFilter={loadPartners} value={filterValue} setValue={setFilterValue}></SearchFilter>
            </div>
            <ConfirmationModal
                message={t('partner:deleteConfirm') + (itemToDelete != null ? itemToDelete.partnerName : '') + '?'}
                isShowing={isConfirmOpen}
                onConfirm={deleteRecord}
                onClose={handleCloseModal}>
            </ConfirmationModal>
            {loading ? (
                <Spinner animation='border' className='container' />
            ) : (<TableComponent
                    model={{
                        partnerName: '',
                        address: '',
                        companyCode: '',
                        pvmCode: '',
                        phoneNumber: '',
                        email: '',
                        contractValidTill: '',
                    }}
                    translationsGroup='partner:'
                    columnsNames={[
                        'partnerName',
                        'address',
                        'companyCode',
                        'pvmCode',
                        'phoneNumber',
                        'email',
                        'contractValidTill',
                    ]}
                    totalElements={partners.totalElements}
                    contents={partners.content}
                    buttons={getButtons()}
                />
            )}
        </>
    );


};